import { Injectable, Injector, OnDestroy } from '@angular/core';
import { BaseObject, CommitChange } from './model/base';

import { Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { GenericUtils } from '@unpispas/upp-defs';

import { UserMode } from '@unpispas/upp-base';
import { adhocService } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';
import { clockService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { viewService} from '@unpispas/upp-base';
import { logsService } from '@unpispas/upp-base';
import { syncService } from './sync';
import { eventsNotifier, LogActions } from './events';

import { DataObject, ViewObject } from './model/base';

import { Session } from './model/objects/session';
import { QrCode } from './model/objects/qrcode';
import { QrScan } from './model/objects/qrscan';
import { User } from './model/objects/user';
import { Place } from './model/objects/place';
import { Address } from './model/objects/address';

import { ObjectFactory } from './model/factory';
import { ObjectRegistry } from './model/model';

import { UserView } from './model/views/user';

/************************************/
/* DATA MODEL REGISTRATION          */
/************************************/

ObjectRegistry();

/************************************/
/* DATA STORAGE                     */
/************************************/

/**
 * Represents the structure of the internal storage for objects.
 */
type StorageMap = {
    [table: string]: Map<string, BaseObject>;
};

/**
 * A utility class for managing objects in a two-tiered storage system.
 * Objects are stored either in a "resolved" state, meaning they have a valid `objid`,
 * or in an "awaiting" state, meaning they lack a valid identifier.
 */
export class dataStorage {

    /**
     * Internal storage divided into `resolved` and `awaiting` categories.
     * - `resolved`: Objects with valid `objid`.
     * - `awaiting`: Objects awaiting resolution (e.g., no valid `objid`).
     */    
    private _objects:{
        resolved: StorageMap;
        awaiting: StorageMap;
    } = {
        resolved: {},
        awaiting: {},
    };

    /********************************/
    /* PRIVATE METHODS              */
    /********************************/

    /**
     * Retrieves an object by table and reference (`objref`) from a specific storage target.
     *
     * @param table - The table name where the object is stored.
     * @param objref - The unique reference of the object (can be `objid` or `_uuid`).
     * @param _target - The target storage to search (`resolved` or `awaiting`).
     * @returns The object if found; otherwise, `null`.
     */    
    private _GetObject(table: string, objref: string, _target: any): BaseObject | null {
        if (!(table in _target)){
            return null;    // table not found
        }

        return _target[table].get(objref) || null;
    }

    /**
     * Adds an object to a specific storage target.
     *
     * @param dataobject - The object to add.
     * @param _target - The target storage (`resolved` or `awaiting`).
     * @returns `true` if the object was added successfully; otherwise, `false`.
     */    
    private _AddObject(dataobject: BaseObject, _target: any) : boolean{
        if (!(dataobject.table in _target)){
            _target[dataobject.table] = new Map <string, BaseObject>();
        }

        if (this._GetObject(dataobject.table, dataobject.objref, _target)){
            return false;   // already exists
        }

        _target[dataobject.table].set(dataobject.objref, dataobject);

        return true;
    }

    /**
     * Deletes an object from a specific storage target.
     *
     * @param dataobject - The object to delete.
     * @param _target - The target storage (`resolved` or `awaiting`).
     */    
    private _DelObject(dataobject: BaseObject, _target: any){
        if (dataobject.table in _target) {
            _target[dataobject.table].delete(dataobject.objref)
        }
    }

    /********************************/
    /* PUBLIC METHODS               */
    /********************************/

    /**
     * Retrieves an object by table, `objid`, or `_uuid` from the storage.
     *
     * @param table - The table name where the object is stored.
     * @param objid - The object identifier (`objid`).
     * @param _uuid - The unique identifier (`_uuid`).
     * @returns The object if found; otherwise, `null`.
     */
    GetByRef(table: string, objid: string | null, _uuid: string | null): BaseObject | null {
        let _object = null;

        if (!_object && _uuid){
            _object = this._GetObject(table, _uuid, this._objects.awaiting);
        }

        if (!_object && (objid != null)){
            _object = this._GetObject(table, String(objid), this._objects.resolved);
        }

        return _object;
    }

    /**
     * Retrieves an object by its reference from either `resolved` or `awaiting` storage.
     *
     * @param dataobject - The object to retrieve.
     * @returns The object if found; otherwise, `null`.
     */    
    GetObject(dataobject: BaseObject): BaseObject | null {
        let _object = null;

        if (!_object){
            _object = this._GetObject(dataobject.table, dataobject.objref, this._objects.awaiting);
        }

        if (!_object){
            _object = this._GetObject(dataobject.table, dataobject.objref, this._objects.resolved);
        }

        return _object;
    }

    /**
     * Adds an object to the appropriate storage (`resolved` or `awaiting`).
     *
     * @param dataobject - The object to add.
     * @returns `true` if the object was added successfully; otherwise, `false`.
     */    
    AddObject(dataobject: BaseObject): boolean {
        if (dataobject.CopyOf){
            return true;    // not suitable for storage 
        }

        if (dataobject.IsVolatile && dataobject.Storage === dataobject.data.store){
            return true;    // in-memory volatile row, not for main storage
        }

        if (!dataobject.ToStorage(this)){
            return true;    // not suitable for storage 
        }

        if (dataobject.objid && !isNaN(Number(dataobject.objid))){
            return this._AddObject(dataobject, this._objects.resolved);
        }
        else {
            return this._AddObject(dataobject, this._objects.awaiting);
        }    
    }

    /**
     * Deletes an object from the storage.
     *
     * @param dataobject - The object to delete.
     */    
    DelObject(dataobject: BaseObject) {
        if (dataobject.objid && !isNaN(Number(dataobject.objid))){
            return this._DelObject(dataobject, this._objects.resolved);
        }
        else {
            return this._DelObject(dataobject, this._objects.awaiting);
        }    
    }

    /**
     * Updates an object in the storage by deleting and re-adding it.
     *
     * @param dataobject - The object to update.
     */    
    SetObject(dataobject: BaseObject | null) {
        if (dataobject){
            this.DelObject(dataobject);
            this.AddObject(dataobject);    
        }
    }

    /**
     * Resolves an object from the `awaiting` storage and moves it to `resolved`.
     *
     * @param dataobject - The object to resolve.
     */
    Resolve(dataobject: BaseObject){
        if (dataobject.objid){
            return;     // this object is already resolved
        }

        this._DelObject(dataobject, this._objects.awaiting);

        dataobject.objid = dataobject.Info['objid'] ? dataobject.Info['objid'].toString() : null;

        const _to_delete: BaseObject | null = this._GetObject(dataobject.table, dataobject.objref, this._objects.resolved);
        const _to_insert: BaseObject | null = dataobject;

        if (_to_delete){
            this._DelObject(_to_delete, this._objects.resolved);            
        }

        if (dataobject.objid){  // object resolution
            this._AddObject(_to_insert, this._objects.resolved);
        }
        else {                  // object replacement
            this._AddObject(_to_insert, this._objects.awaiting);
        }

        if (_to_delete){
            _to_delete.OnResolve();   // resolve references for the deleted object            
        }
    }

    /**
     * Replaces an object in the `resolved` storage with a new version.
     *
     * @param dataobject - The updated object.
     * @param uuid - The UUID of the object to replace.
     */
    Replace(dataobject: BaseObject, uuid: string){
        const _to_update = this._GetObject(dataobject.table, uuid, this._objects.awaiting);
        const _to_delete: BaseObject | null = this._GetObject(dataobject.table, dataobject.objref, this._objects.resolved);
        const _to_insert: BaseObject | null = dataobject;
        
        if (_to_update){
            this._DelObject(_to_update, this._objects.awaiting);
        }

        if (_to_delete){
            this._DelObject(_to_delete, this._objects.resolved); 
        }

        this._AddObject(_to_insert, this._objects.resolved)

        if (_to_update){
            _to_update.OnResolve();     // resolve references for the updated object
        }
    }

    /**
     * Clears all objects from the storage.
     */    
    Clear(){
        this._objects.resolved = {};
        this._objects.awaiting = {};
    }
}

/**
 * Represents a transaction for committing data changes.
 */
export class dataTransaction {
    private _trstatus: 'OPEN' | 'CLOSED' = 'OPEN';
    private _tocommit = new Map <string, CommitChange> ();

    constructor(private data: dataService){
        // nothing to do
    }

    /**
     * Adds an item to the transaction.
     * 
     * @param item - The object to add.
     * @returns A promise resolving to `true` if successful.
     * @throws If transaction state is invalid or dependencies are unmet.
     */    
    async push(item: BaseObject, force = false) : Promise<boolean> {
        if (this._trstatus != 'OPEN'){
            throw new Error("[TRANSACTION]: invalid transaction state: '" + this._trstatus + "'")
        }

        if (force){
            item.ForceUpdate();
        }

        const _changes: CommitChange[] = await Promise.all(item.Commit);
        for (const _change of _changes) {
            if (_change.entrynfo == null){
                continue;    // already resolved in this transaction
            }
            else {
                this._tocommit.set(_change.relation.table + '@' + (_change.relation.objid || _change.relation._uuid), _change)
            }
        }

        // determine if the item is safe to be commited
        for (const _change of _changes){
            // check if the change is suitable for the transaction
            for (const _key in _change.requires){
                const _require = _change.requires[_key];

                let _issafe = ('objid' in _require.relation) && (_require.relation.objid !== null);

                if (!_issafe){     // check if the new item is included in this transaction's commit
                    _issafe = this._tocommit.has(_require.relation.table + '@' + _require.relation._uuid); 
                }
                
                if (!_issafe){     // check if the new item is included in the list of items to be commited
                    _issafe = this.data.ToCommit.has(_require.relation.table + '@' + _require.relation._uuid);
                }
    
                if (!_issafe){      // check if the new item has been sent to server (awaiting response)
                    const _stored = this.data.store.GetByRef(_require.relation.table, null, _require.relation._uuid);
                    if (_stored){
                        _issafe = _stored.InCommit;
                    }
                }
    
                if (!_issafe){
                    throw new Error("[TRANSACTION]: invalid item in transaction (dependencies don't met)");
                }    
            }
        }

        return true;        
    }

    /**
     * Commits all items in the transaction.
     * 
     * @param force - If `true`, forces the commit process.
     * @returns A promise resolving to `true` when complete.
     */    
    async flush(force = false): Promise <boolean> {
        if (this._trstatus != 'OPEN'){
            throw new Error("[TRANSACTION]: invalid transaction state: '" + this._trstatus + "'")
        }

        // add the computed changes in transacion to the data commit
        for(const _change of this._tocommit.values()){
            this.data.ToCommit.set(_change.relation.table + '@' + (_change.relation.objid || _change.relation._uuid), _change);
        }

        return await this.data.Flush(force);
    }
}

/**
 * Manages weak references to `ViewObject<DataObject>` instances, ensuring that
 * objects remain in memory only while actively used. When the last reference
 * is lost, the object is automatically garbage collected.
 *
 * This class uses `WeakMap` and `WeakRef` to store objects without preventing
 * their garbage collection, avoiding memory leaks.
 */
export class aliveItems {
    // WeakMap stores objects with weak references
    private _alivemap = new WeakMap<DataObject, WeakRef<ViewObject<DataObject>>>();

    // Debug information
    private _register_count = 0;
    private _released_count = 0;

    get itemssize(): number {
        return this._register_count - this._released_count;
    }

    logstatus(): void {
        console.group('[ALIVE] Cache state');
        console.info(`Total register: ${this._register_count}`);
        console.info(`Total released: ${this._released_count}`);
        console.groupEnd();
    }

    // Registry to clean up references when objects are garbage collected
    private finalizationRegistry = new FinalizationRegistry <DataObject> (
    (object) => {
        if (GenericUtils.VerboseLogging(3)){
            console.info("-- [ALIVE] " + object.table + "@" + object._uuid + " released (current size: " + this.itemssize + ")");
        }

        this._released_count++;
        this._alivemap.delete(object);
    });

    /**
     * Creates and stores a weak reference to a ViewObject if it does not exist.
     * The ViewObject remains in memory only if there are active references.
     *
     * @param object The DataObject instance to store.
     * @returns The ViewObject instance, or null if not found.
     */    
    private set(object: DataObject): ViewObject<DataObject> | null {
        const _view = object.View;
        if (_view){
            if (GenericUtils.VerboseLogging(3)){
                console.info("++ [ALIVE] " + object.table + "@" + object._uuid + " included (current size: " + this.itemssize + ")");
            }

            this._register_count++;
            
            this._alivemap.set(object, new WeakRef(_view));
            this.finalizationRegistry.register(_view, object);
        }
        return _view;
    }

    /**
     * Checks if the DataObject has an associated ViewObject in the weak storage.
     *
     * @param object The DataObject instance.
     * @returns True if the ViewObject is still in memory, false otherwise.
     */    
    has (object: DataObject): boolean {
        const _hasrf = this._alivemap.has(object);
        const _alive = this._alivemap.get(object)?.deref() !== undefined;
        
        if (_hasrf && !_alive) {
            console.info("[ALIVE] " + object.table + "@" + object._uuid + " reference exists but object is gone");
        }
        
        return _hasrf && _alive;
    }

    /**
     * Retrieves the ViewObject associated with a DataObject.
     * If it does not exist, it is created and stored with a weak reference.
     *
     * @param object The DataObject instance.
     * @returns The ViewObject instance, or null if the object is null.
     */    
    get(object: DataObject | null) : ViewObject<DataObject> | null {
        if (!object){
            return null;
        }

        let _view = this._alivemap.get(object)?.deref() || null;
        if (_view == null){
            _view = this.set(object);
            if (_view){  // perform the registration just after creation
                _view.doRegister();
            }
        }
        return _view;
    }
}

/**
 * Service to manage data synchronization, transactions, and session-related operations.
 */
@Injectable({
    providedIn: 'root'
})
export class dataService implements OnDestroy {
    private _user_subscription: Subscription | null = null;

    private _expired_subscription: Subscription | null = null;
    private _onchange_subscription: Subscription | null = null;
    private _onreload_subscription: Subscription | null = null;

    /**
     * Constructor for the dataService.
     * 
     * @param injector - The angular dependencies injector
     * @param clock - Service to manage time-related operations.
     * @param lang - Service to manage language-related operations.
     * @param adhoc - Ad-hoc request handling service.
     * @param logs - Service to manage logs and logging operations.
     * @param events - Service to manage application-wide events.
     * @param state - Service to manage application state.
     * @param view - Service to manage the application's view layer.
     * @param sync - Service to manage synchronization with the backend server.
     */    
    constructor(public injector: Injector, public clock: clockService, private lang: languageService, private adhoc: adhocService, private logs: logsService, private state: stateService, private view: viewService, private sync: syncService){
        this.sync.UnloadHandler = () => {
            if ((this.state.Access == 'LOGIN') && this.place){
                this.events.LogAction(LogActions.LOGIN, {
                    place: this.place,
                    login: false
                });
            }

            const _guestScan = this.sync.qrscan || this.qrcode?.qrscan || null;
            if ((this.state.Access == 'GUEST') && _guestScan){
                this.events.LogAction(LogActions.GUEST, {
                    table: _guestScan,
                    login: false
                });
            }
        };

        // Let sync CheckChanges accept requires whose parent is only InCommit in the live store
        // (same rule as data.Commit), without importing dataService into syncService.
        this.sync.IsInCommit = (table: string, uuid: string) => {
            const _stored = this.store.GetByRef(table, null, uuid);
            return !!(_stored && _stored.InCommit);
        };

        this._expired_subscription = this.sync.OnExpired.subscribe(
        () => {
            this.Stop(true);                
        });

        this._onchange_subscription = this.sync.OnChange.subscribe(
        (change) => {
            const _uuid = (change['table'] == 'SESSION') ? change['id'] : change['_uuid'];
            const _objid = change['objid'] == null ? null : String(change['objid']);
            
            let _update = this.store.GetByRef(change['table'], _objid, _uuid);
            if (!_update){  // include the object into the data model (if it doesn't exist)
                _update = ObjectFactory.object(change['table'], _objid, this);
                if (_update){
                    this.store.AddObject(_update);
                }
            }

            if (_update){   // call to the update method of the target object
                _update.OnChange(change);
            }
            else {
                console.error("[DATA] Discarded change (could not create object)", change);
            }            
        });
    }

    /**
     * Cleanup logic when the service is destroyed.
     */    
    ngOnDestroy(): void {
        this.sync.UnloadHandler = null;
        this.sync.IsInCommit = null;

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._expired_subscription){
            this._expired_subscription.unsubscribe();
            this._expired_subscription = null;
        }

        if (this._onchange_subscription){
            this._onchange_subscription.unsubscribe();
            this._onchange_subscription = null;
        }

        if (this._onreload_subscription){
            this._onreload_subscription.unsubscribe();
            this._onreload_subscription = null;
        }
    }

    /************************************/
    /* STORAGE ACCESS                   */
    /************************************/

    private _storage: dataStorage | null = null;

    /**
     * Lazy getter for the data storage instance.
     * Initializes a new `dataStorage` instance if it doesn't already exist.
     */    
    get store(): dataStorage {
        if (!this._storage){
            this._storage = new dataStorage();
        }
        return this._storage;
    }

    /************************************/
    /* EVENTS ACCESS                    */
    /************************************/

    private _events: eventsNotifier | null = null;

    /**
     * Lazy getter for the event factory instance.
     * Initializes a new `events` instance if it doesn't already exist.
     */    
    get events(): eventsNotifier {
        if (!this._events){
            this._events = new eventsNotifier(this);
        }
        return this._events;
    }

    /************************************/
    /* ALIVE ITEMS MAP                  */
    /************************************/

    /**
     * Lazy getter for the alive items repository
     * Initializes a new `aliveItems` instance if it doesn't already exist.
     */     
    private _alives: aliveItems | null = null;
    get alives(): aliveItems {
        if (!this._alives){
            this._alives = new aliveItems();
        }
        return this._alives;
    }

    /************************************/
    /* TRANSACTIONS                     */
    /************************************/

    get transaction(): dataTransaction {
        return new dataTransaction(this);
    }

    /************************************/
    /* INITIALIZATION / FINALIZATION    */
    /************************************/

    private _reloadRequested = new Subject<void> ();
    private _onSessionReleased = new Subject<boolean>();
    private _onSessionChanged = new Subject<Session>();
    
    /** Observable triggered when a reload is requested. */
    public OnReloadRequested = this._reloadRequested.asObservable();
    /** Observable triggered when the session is released. */    
    public OnSessionReleased = this._onSessionReleased.asObservable();
    /** Observable triggered when the session changes. */    
    public OnSessionChanged = this._onSessionChanged.asObservable();

    private _session: Session | null = null;

    /**
     * Retrieves the current session.
     */    
    get session(): Session | null {
        return this._session;
    }

    /** Current session device identifier. */
    get device(): string | null {
        return this.state.device;
    }

    /**
     * Retrieves the current serial number.
     */    
    get serial(): string {
        if (this.place){
            return GenericUtils.CRC32(this.place.objid + '_' + this.state.device).toString(16).toUpperCase().padStart(8, '0');
        }

        throw new Error("[DATA] Service not ready to provide a serial number!");
    }

    /**
     * Waits until the syncronization service is ready
     */ 
    async Ready(): Promise<void> {
        await firstValueFrom(this.sync.IsReady);
    }

    /**
     * Starts a session and initializes the necessary components.
     * 
     * @param session - The session identifier.
     * @param access - The access mode for the session (`LOGIN` or `GUEST`).
     * @returns A promise resolving to `true` if the session starts successfully, otherwise `false`.
     */    
    async Start(session: string, access: UserMode): Promise <boolean> {
        if (this.session && this.session.id == session){
            return false;   // already started
        }

        this._session = new Session(null, this, null, session);
        if (this.session && this.store.AddObject(this.session)){
            const _onupdated = firstValueFrom(this._session.OnUpdated);
            
            await this.sync.Start(session);
            this.state.Access = access;
            if (this.session){
                this._onSessionChanged.next(this.session);
            }

            await _onupdated;
        }
        else {
            throw new Error("[DATA] Could not start session!")
        }

        /** session hydrated from cache and/or server */
        await new Promise<void>((resolve) => {
            setTimeout(async () => {
                if (this.session){
                    if (access == 'LOGIN'){
                        const _user = this.session.user as User;
                        await this.SetUser(_user);
                        if (this.user){
                            this.view.View = 'USER';
                        }
                    }

                    if (access == 'GUEST'){
                        await this.SetQrScan(this.session.qrscan ?? null);
                        if (this.qrcode){
                            this.view.View = 'PLACE';
                        }
                    }
                }

                resolve();
            }, 0);
        });

        return true;
    }

    /**
     * Stops the current session and resets all session-related data.
     * 
     * @returns A promise resolving to `true` after successful session termination.
     */    
    async Stop(expired: boolean): Promise <boolean>{
        if (this._session){
            this._session.DoUpdate();
        }

        await this.SetPlace(null);
        await this.SetUser(null);
        await this.SetQrScan(null);

        this._session = null;

        this.sync.OnReleased.pipe(take(1)).subscribe(
        () => {
            this._onSessionReleased.next(expired);
            setTimeout(() => {
                window.location.reload();
            }, 0);
        });

        await this.sync.Stop();

        this.logs.Stop();
        this.store.Clear();

        return true;
    }

    /********************************/
    /* SESSION USER                 */
    /********************************/

    private _onUserChanged = new Subject<User | null>();

    /**
     * Observable triggered when the user changes.
     */    
    OnUserChanged = this._onUserChanged.asObservable();

    private _user: User | null = null;

    /**
     * Retrieves the current user.
     */    
    get user(): User | null {
        return this._user;
    }

    /**
     * Updates the current user and notifies related services.
     * 
     * @param user - The new user to set.
     * @returns A promise resolving to `true` after successful update.
     */    
    async SetUser(user: User | null): Promise <boolean>{
        await user?.WaitLoaded();

        if (this._user != user){
            this.store.SetObject(user);

            await this.sync.ResetStage();
            this._user = user;

            if (await this.sync.SetUser(user)) {
                this._onUserChanged.next(user);
            }

            if (user){
                await this.lang.SetLanguage(user.lang || 'ES');
    
                // notify user online status to other clients
                const _user = this.alives.get(user || this._user) as UserView;
                if (_user.employees.length > 0){
                    const _transaction = new dataTransaction(this);
                    if (_transaction){    
                        for(const employee of _user.employees){
                            await _transaction.push(employee.object, true);
                        }    
    
                        _transaction.flush(true);
                    }    
                }
            }
        
            if (this.user){
                if (this.user.objid){
                    console.info("[USER] " + (this.user.fullname || this.user.email) + " (language: " + this.user.lang + ")");
                }
                else {
                    console.info("[USER] (guest user)")
                }
    
                this._user_subscription = this.user.OnRefresh.subscribe(
                () => {
                    if (this.user?.lang){
                        this.lang.SetLanguage(this.user.lang);
                    }
                })
            }
            else {
                if (this._user_subscription){
                    this._user_subscription.unsubscribe();
                    this._user_subscription = null;
                }
            }    
        }

        this._reloadRequested.next();

        return true;
    }

    /********************************/
    /* SESSION PLACE                */
    /********************************/

    private _onPlaceChanged = new Subject<Place | null>();

    /**
     * Observable triggered when the place changes.
     */    
    OnPlaceChanged = this._onPlaceChanged.asObservable();

    private _place: Place | null = null;
    get place(): Place | null {
        return this._place;
    }

    /**
     * Updates the current place and notifies related services.
     * 
     * @param place - The new place to set.
     * @returns A promise resolving to `true` after successful update.
     */    
    async SetPlace(place: Place | null): Promise <boolean>{
        await place?.WaitLoaded();

        if (this.place != place){
            this.store.SetObject(place);
            
            let _notifylogin = true;
            if ((place && place.ToInsert) || (!place && this.place && this.place.ToInsert)){
                _notifylogin = false;   // these are a place creation attempts
            } 

            // add the login event to the registry
            if (_notifylogin){
                this.events.LogAction(LogActions.LOGIN, {
                    place: place ? place : this.place,
                    login: place ? true : false
                })    
            }

            await this.sync.ResetStage();
            this._place = place;

            // preload the place language strings
            if (place){
                let _placelang = 'ES';  // defaults to 'ES'

                const _placectcd = (place.address as Address)?.country;
                const _placecode = (_placectcd || '').toUpperCase();
                if (this.lang.GetLanguages().includes(_placecode)){
                    _placelang = _placecode;
                }

                this.lang.SetLanguage(_placelang, false);
            }
            
            this.sync.SetPlace(place).then(
            () => {
                this._onPlaceChanged.next(place);
                if (place != null){
                    place.Initialize();
                    if (this.device){
                        place.Campaign(this.device);
                    }
                    this.view.View = 'PLACE';
                }                  
            });
            
            this._reloadRequested.next();
        }
        else {  // update the state place to be considered in doRequest
            if (place){
                this.state.place = place.objid;
            }
        }

        return true;
    }

    /********************************/
    /* SESSION QRSCAN               */
    /********************************/

    /**
     * Binds the guest scan and its tenant mesa (`qrscan.qrcode`).
     * Only chains `SetQrCode` when the mesa is known (or clearing); a scan
     * without `qrcode` yet must not wipe a pending mesa trigger with `null`.
     */
    async SetQrScan(qrscan: QrScan | null): Promise <boolean>{
        const _bound = await this.sync.SetQrScan(qrscan);
        if (!_bound){
            return false;
        }

        if (!qrscan){
            return this.SetQrCode(null);
        }

        if (qrscan.qrcode){
            return this.SetQrCode(qrscan.qrcode);
        }

        return true;
    }

    /********************************/
    /* SESSION QRCODE               */
    /********************************/

    private _onQrCodeChanged = new Subject<QrCode | null>();

    /**
     * Observable triggered when the table changes.
     */    
    OnQrCodeChanged = this._onQrCodeChanged.asObservable();

    private _qrcode: QrCode | null = null;

    /**
     * Retrieves the current table.
     */    
    get qrcode(): QrCode | null {
        return this._qrcode;
    }

    /**
     * Updates the current table and notifies related services.
     * 
     * @param qrcode - The new table to set.
     * @returns A promise resolving to `true` after successful update.
     */    
    async SetQrCode(qrcode: QrCode | null): Promise <boolean>{
        if (qrcode?.IsLoaded) {
            await qrcode.WaitLoaded();
        }

        if (this.qrcode != qrcode){
            const _previous = this.qrcode;
            this.store.SetObject(qrcode);

            // Registry l3/l4: central QRSCAN.objid (server register_guest_event).
            const _guestScan = (qrcode?.qrscan || _previous?.qrscan || this.sync.qrscan) || null;
            if (_guestScan){
                this.events.LogAction(LogActions.GUEST, {
                    table: _guestScan,
                    login: !!qrcode
                });
            }

            await this.sync.ResetStage();
            this._qrcode = qrcode;

            await this.sync.SetQrCode(qrcode);
            this._onQrCodeChanged.next(qrcode);
        
            // Notify table online status only with a hydrated row (empty stubs
            // would wipe QRCODE columns and fail with ApplicationError::Database).
            const _notify = qrcode || _previous;
            if (_notify?.objid && !_notify.IsLoaded && this.state.place) {
                const _data: any = await this.FetchByObjid(_notify.table, _notify.objid);
                if (_data?.['errorcode'] == 0 && _data['info']) {
                    _notify.OnChange({ ..._data['info'], table: _notify.table });
                }
            }
            if (_notify?.IsLoaded) {
                await _notify.ForceCommit();
            }

            this._reloadRequested.next();
        }

        return true;
    }

    /************************************/
    /* SERVER REQUESTS                  */
    /************************************/

    private _doFetchhUrl = 'sync/objidrequest';

    /**
     * Observable triggered when the load percentage changes.
     */    
    get OnRefreshPercent(): Observable <number> {
        return this.sync.synchonizer.pending.OnRefreshPercent;
    }   

    /**
     * Current pending-queue hydration stage (0…9). PTF work uses stage ≥ 2 as
     * “floor ready” (RN-SYNC-33 / C3).
     */
    get RefreshStage(): number {
        return this.sync.synchonizer.pending.Stage;
    }

    /**
     * Emits when {@link RefreshStage} advances.
     */
    get OnRefreshStage(): Observable <number> {
        return this.sync.synchonizer.pending.OnRefreshStage;
    }

    /**
     * Observable triggered when a refresh operation is completed.
     */    
    get OnRefreshCompleted(): Observable <void> {
        return this.sync.synchonizer.OnRefreshCompleted;
    }

    /**
     * Observable triggered when a commit operation is completed.
     */    
    get OnCommitCompleted(): Observable <void> {
        return this.sync.synchonizer.OnCommitCompleted;
    }

    /**
     * Fetches an object by its `objid` from the backend server.
     * 
     * @param table - The table name where the object resides.
     * @param objid - The `objid` of the object to fetch.
     * @returns A promise resolving with the fetched object data.
     */    
    FetchByObjid(table: string, objid: string) : Promise <any> {
        return this.adhoc.DoRequest(this._doFetchhUrl, { table: table, objid: objid }, null, false);
    }

    /**
     * Loads partial updates for a table and object ids.
     *
     * @param table - Root table for requested objects.
     * @param objids - Root object ids to request.
     * @param storage - Optional contextual storage to preserve canonical instances.
     * @param completed - Optional completed marker returned by server.
     * @returns Partial roots from the contextual graph.
     */
    async FetchByItem(table: string, objids: Array<string>, storage?: dataStorage, completed?: any): Promise<DataObject[]> {
        const _objids = Array.from(new Set(objids.map((objid) => objid))).filter((objid) => !isNaN(Number(objid)));
        if (_objids.length === 0){
            return [];
        }

        const _storage = storage ?? new dataStorage();
        const _rootsid = new Map<string, DataObject>();
        const _pending: string[] = [];

        for (const _objid of _objids){
            const _stored = (storage ? _storage.GetByRef(table, _objid, null) : (_storage.GetByRef(table, _objid, null) || this.store.GetByRef(table, _objid, null))) as DataObject | null;
            if (_stored && !_stored.IsPartial){
                _rootsid.set(_objid, _stored);
                continue;
            }

            _pending.push(_objid);
        }

        let _completed = completed ?? null;
        if (_pending.length > 0){
            const _request = JSON.stringify({
                items: _pending.map((objid) => ({ table, objid }))
            });

            const _params = (completed != null) ? { timestamp: completed } : null;
            const _data: any = await this.adhoc.DoRequest('reports/complete', _params, _request, false);
            if ((_data['errorcode'] !== 0) || !Array.isArray(_data['updates'])){
                return [];
            }

            _completed = _data['completed'] ?? null;

            for (const _update of _data['updates'] || []){
                const _table = String(_update?.['table']);
                const _objid = String(_update?.['objid']);

                if (!_table || !_objid){
                    throw new Error('[ERROR] FetchByItem received update without table or objid: ' + JSON.stringify(_update));
                }

                // Reuse the canonical instance (isolated storage first, then global
                // catalog); only brand-new objects must be registered so the same
                // instance is later resolved as the root and by child relations.
                let _object = (_storage.GetByRef(_table, _objid, null) || this.store.GetByRef(_table, _objid, null)) as DataObject | null;
                if (!_object){
                    _object = ObjectFactory.object(_table, _objid, this, {
                        partial: true,
                        storage: _storage
                    });
                    _storage.AddObject(_object);
                }

                _object.OnChange(_update);
                _object.Completed = _completed;
            }
        }

        for (const _objid of _pending){
            const _root = ObjectFactory.object(table, _objid, this, {
                partial: true,
                storage: _storage
            });
            _root.Completed = _completed;
            _rootsid.set(_objid, _root);
        }

        const _roots: DataObject[] = [];
        for (const _objid of _objids){
            const _root = _rootsid.get(_objid);
            if (_root){
                _roots.push(_root);
            }
        }

        return _roots;
    }

    /************************************/
    /* PROCESS SERVER UPDATES           */
    /************************************/

    private _logchanges = false;    // enable verbose logging additional detail

    /**
     * Logs detailed information about changes made during a commit operation.
     * 
     * @param _change - The change to log.
     */    
    private _LogChange(_change: CommitChange) {
        if (this._logchanges){
            const _entrynfo = _change.entrynfo;

            const _newentry = !(('objid' in _entrynfo) && _entrynfo['objid']);
            if (_newentry){
                console.info("[COMMIT] INSERT - '" + _entrynfo['table'] + "'");
            }
            else {
                console.info("[COMMIT] UPDATE - '" + _entrynfo['table'] + " (with objid [" + _entrynfo['objid'] + "]'");
            }
    
            for (const _field in _entrynfo){
                if (['table', 'objid'].includes(_field)){
                    continue;
                }
    
                console.info(" - " + _field + ": " + _entrynfo[_field]);
            }
        }
    }

    private _tocommit = new Map <string, CommitChange>  ();

    /**
     * Retrieves the list of objects pending commit.
     */    
    get ToCommit(): Map <string, CommitChange> {
        return this._tocommit;
    }

    /**
     * Adds an list of changes to the commit queue.
     * 
     * @param item - The object to commit.
     * @throws If the object has unresolved dependencies.
     */    
    async Commit(commits: Promise <CommitChange> []): Promise <boolean> {
        const _changes: CommitChange[] = await Promise.all(commits);
        for (const _change of _changes){
            // check if the change is suitable to be commited
            for (const _key in _change.requires){
                const _require = _change.requires[_key];
    
                let _issafe = ('objid' in _require.relation) && (_require.relation.objid);

                if (!_issafe){     // check if the new item is included in the list of items to be commited
                    _issafe = this.ToCommit.has(_require.relation.table + '@' + _require.relation._uuid);
                }

                if (!_issafe){     // check if the new item has been sent to server (awaiting response)
                    const _stored = this.store.GetByRef(_require.relation.table, null, _require.relation._uuid);
                    if (_stored){
                        _issafe = _stored.InCommit;
                    }                    
                }  
                
                if (!_issafe){
                    throw new Error("[DATA]: invalid item to be commited (dependencies doesn't met)")
                }                
            }

            const _index = _change.relation.objid || _change.relation._uuid;
            if (!_index){
                throw new Error("[DATA]: invalid item to be commited (unique index not provided)")
            }

            this._tocommit.set(_change.relation.table + '@' + _index, _change);
        }

        return true;
    }

   /**
     * Commits all changes in the queue to the backend server.
     * 
     * @param force - If `true`, forces the commit process.
     * @returns A promise resolving to `true` after successful synchronization.
     */
    async Flush(force = false): Promise <boolean>{
        let _flushed = false;

        const _changes: CommitChange[] = [...this.ToCommit.values()];
        if (_changes.length > 0){
            if (GenericUtils.VerboseLogging(2)){
                for (const _change of _changes){
                    this._LogChange(_change);
                }    
            }

            _flushed = await this.sync.Flush(_changes, force);
            // Keep ToCommit when sync rejects the package so the caller can retry
            // and UI can avoid treating a failed flush as a successful close.
            if (_flushed){
                this._tocommit.clear();
            }
        }
        else {
            console.warn("[DATA] Could not commit (nothing to be commited)");
        }

        return _flushed;
    }
}